import { api } from "@/lib/api";
import type { TaskPriority } from "@/services/email-to-task.service";

export type InboundEmailStatus =
	| "received"
	| "processing"
	| "ready"
	| "approving"
	| "approved"
	| "rejected"
	| "failed"
	| "ignored";

export interface DraftSubtask {
	include: boolean;
	title: string;
	description: string | null;
	due_date: string | null;
	assignee_ids: string[];
	unresolved_names: string[];
	checklist: string[];
	warnings: string[];
}

export interface DraftGroup {
	include: boolean;
	title: string;
	description: string | null;
	project_id: string | null;
	priority: TaskPriority | null;
	due_date: string | null;
	assignee_ids: string[];
	unresolved_names: string[];
	warnings: string[];
	subtasks: DraftSubtask[];
}

export interface NonTask {
	text: string;
	kind: "question" | "fyi" | "praise";
}

export interface EmailDraft {
	summary: string | null;
	body_truncated: boolean;
	groups: DraftGroup[];
	non_tasks: NonTask[];
}

export interface InboundEmailSummary {
	id: string;
	from_email: string;
	from_name: string | null;
	subject: string | null;
	status: InboundEmailStatus;
	ignore_reason: string | null;
	error: string | null;
	attempts: number;
	sent_at: string | null;
	created_at: string;
	reviewed_at: string | null;
	created_task_ids: string[];
}

export interface InboundEmail extends InboundEmailSummary {
	recipients: { name: string | null; email: string; kind: "to" | "cc" }[];
	body_text: string;
	body_truncated: boolean;
	draft: EmailDraft | null;
	agent_run: { agent_name: string | null; runs: number; duration_ms: number } | null;
	approval_stale: boolean;
	updated_at: string;
}

export interface DirectoryUser {
	id: string;
	email: string;
	full_name: string | null;
}

export interface DirectoryProject {
	id: string;
	key: string | null;
	name: string;
}

export interface InboxDirectory {
	users: DirectoryUser[];
	projects: DirectoryProject[];
}

type Envelope<T> = { success: boolean; message?: string; data: T };

export async function listInboundEmails(statuses: InboundEmailStatus[]): Promise<InboundEmailSummary[]> {
	const res = await api.get<Envelope<{ emails: InboundEmailSummary[] }>>(
		`/inbound-emails?status=${statuses.join(",")}`,
	);
	return res.data.emails;
}

export async function getInboundEmail(
	id: string,
): Promise<{ email: InboundEmail; directory: InboxDirectory }> {
	const res = await api.get<Envelope<{ email: InboundEmail; directory: InboxDirectory }>>(
		`/inbound-emails/${id}`,
	);
	return res.data;
}

export async function saveInboundDraft(id: string, draft: EmailDraft): Promise<InboundEmail> {
	const res = await api.patch<Envelope<{ email: InboundEmail }>>(`/inbound-emails/${id}/draft`, { draft });
	return res.data.email;
}

export async function approveInboundEmail(id: string, draft: EmailDraft): Promise<string> {
	const res = await api.post<Envelope<unknown>>(`/inbound-emails/${id}/approve`, { draft });
	return res.message ?? "Tasks created.";
}

export async function rejectInboundEmail(id: string): Promise<void> {
	await api.post(`/inbound-emails/${id}/reject`, {});
}

export async function reprocessInboundEmail(id: string): Promise<string> {
	const res = await api.post<Envelope<unknown>>(`/inbound-emails/${id}/reprocess`, {});
	return res.message ?? "Queued.";
}

/** Email Parser: runs the agent on pasted text and stores the draft for review. */
export async function parseEmail(payload: { text: string; subject?: string; sender?: string }): Promise<InboundEmail> {
	const res = await api.post<Envelope<{ email: InboundEmail }>>("/inbound-emails/manual", payload);
	return res.data.email;
}
